import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface LessonRow {
  readonly lessonLearnedId: string;
  readonly lessonCode: string;
  readonly project: string;
  readonly event: string;
  readonly isMajorProject: boolean;
  readonly approvalStatus: string;
  readonly status: string;
}

const STATUSES = ['', 'DRAFT', 'IN_REVIEW', 'APPROVED', 'PUBLISHED', 'REJECTED', 'ARCHIVED', 'RETIRED', 'OBSOLETE'];
const APPROVAL = ['', 'PENDING', 'APPROVED', 'REJECTED'];
const MAJOR = ['', 'true', 'false'];

/**
 * M35 — Lessons Learned Dashboard (§33). Register of project lessons learned
 * (T468) carrying the §16 approval status and the §8 lifecycle, and flagging
 * major-project lessons which must be fully documented before approval
 * (BR-05). Filterable by lifecycle status, approval status, major-project flag
 * and free-text search. Tenant isolation is enforced server-side (LAW 4).
 */
export function LessonsLearnedDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<LessonRow[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [approvalStatus, setApprovalStatus] = useState('');
  const [isMajorProject, setIsMajorProject] = useState('');
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [newProject, setNewProject] = useState('');
  const [newEvent, setNewEvent] = useState('');
  const [newMajor, setNewMajor] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (approvalStatus) params.set('approvalStatus', approvalStatus);
    if (isMajorProject) params.set('isMajorProject', isMajorProject);
    if (q) params.set('q', q);
    const qs = params.toString();
    apiRequest<{ data: LessonRow[]; total: number }>(`/v1/kms/lessons${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, status, approvalStatus, isMajorProject, q]);

  useEffect(() => load(), [load]);

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newProject.trim()) { setActionError(t('m35.lesson.projectRequired')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/kms/lessons`, {
        method: 'POST',
        body: { project: newProject.trim(), event: newEvent.trim() || undefined, isMajorProject: newMajor },
        token,
      });
      setNotice(t('m35.common.created', { name: newProject.trim() }));
      setNewProject('');
      setNewEvent('');
      setNewMajor(false);
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m35.lesson.notAuthorisedManage'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: LessonRow, op: string, label: string, perm: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    setBusy(true);
    try {
      await apiRequest(`/v1/kms/lessons/${r.lessonLearnedId}/${op}`, { method: 'POST', token });
      setNotice(t('m35.common.notice', { code: r.lessonCode, label }));
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m35.common.notAuthorisedAction', { perm }));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r: LessonRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    setBusy(true);
    try {
      await apiRequest(`/v1/kms/lessons/${r.lessonLearnedId}`, { method: 'DELETE', token });
      setNotice(t('m35.common.notice', { code: r.lessonCode, label: t('m35.common.act.deleted') }));
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m35.lesson.notAuthorisedManage'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <main aria-label="lessons-dashboard">
      <h1>{t('m35.lesson.title')}</h1>

      <form aria-label="lesson-form" onSubmit={(e) => void create(e)}>
        <label>
          {t('m35.lesson.project')}
          <input aria-label="field-lesson-project" value={newProject} onChange={(e) => setNewProject(e.target.value)} />
        </label>
        <label>
          {t('m35.lesson.event')}
          <input aria-label="field-lesson-event" value={newEvent} onChange={(e) => setNewEvent(e.target.value)} />
        </label>
        <label>
          {t('m35.lesson.majorProject')}
          <input type="checkbox" aria-label="field-lesson-major" checked={newMajor} onChange={(e) => setNewMajor(e.target.checked)} />
        </label>
        <Button variant="primary" type="submit" aria-label="submit-lesson" disabled={busy}>{busy ? t('m35.common.saving') : t('m35.lesson.createBtn')}</Button>
      </form>
      {actionError && <p role="alert" aria-label="lesson-action-error">{actionError}</p>}
      {notice && <p aria-label="lesson-action-notice">{notice}</p>}

      <div className="filters">
        <label>
          {t('m35.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m35.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m35.common.approval')}
          <select aria-label="filter-approval" value={approvalStatus} onChange={(e) => setApprovalStatus(e.target.value)}>
            {APPROVAL.map((s) => <option key={s || 'all'} value={s}>{s || t('m35.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m35.lesson.majorProject')}
          <select aria-label="filter-major" value={isMajorProject} onChange={(e) => setIsMajorProject(e.target.value)}>
            {MAJOR.map((s) => <option key={s || 'all'} value={s}>{s === '' ? t('m35.common.all') : s === 'true' ? t('m35.common.yes') : t('m35.common.no')}</option>)}
          </select>
        </label>
        <label>
          {t('m35.common.search')}
          <input aria-label="filter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('m35.lesson.searchPh')} />
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m35.common.loading')}</p> : (
        <>
          <p aria-label="lesson-count">{t('m35.lesson.count', { n: total })}</p>
          <DataTable<LessonRow>
            ariaLabel="lesson-table"
            testId="lesson-table"
            rowKey={(r) => r.lessonLearnedId}
            rowDataAttrs={(r) => ({ 'data-status': r.status, 'data-approval': r.approvalStatus })}
            rows={rows}
            columns={[
              { key: 'code', header: t('m35.common.code'), render: (r) => r.lessonCode },
              { key: 'project', header: t('m35.lesson.project'), render: (r) => r.project },
              { key: 'event', header: t('m35.lesson.event'), render: (r) => r.event },
              { key: 'major', header: t('m35.lesson.colMajor'), render: (r) => r.isMajorProject ? t('m35.common.yes') : t('m35.common.no'), cellAriaLabel: (r) => `lesson-major-${r.lessonCode}` },
              { key: 'approval', header: t('m35.common.approval'), render: (r) => r.approvalStatus },
              { key: 'status', header: t('m35.common.status'), render: (r) => r.status },
              { key: 'actions', header: t('m35.common.actions'), render: (r) => (
                <>
                  {r.approvalStatus === 'PENDING' && (
                    <Button variant="secondary" type="button" aria-label={`approve-${r.lessonCode}`} disabled={busy} onClick={() => void act(r, 'approve', t('m35.common.act.approved'), 'M35_LESSON_APPROVE')}>{t('m35.common.btnApprove')}</Button>
                  )}
                  {r.approvalStatus === 'APPROVED' && r.status !== 'PUBLISHED' && (
                    <Button variant="primary" type="button" aria-label={`publish-${r.lessonCode}`} disabled={busy} onClick={() => void act(r, 'publish', t('m35.common.act.published'), 'M35_LESSON_APPROVE')}>{t('m35.common.btnPublish')}</Button>
                  )}
                  {r.status !== 'PUBLISHED' && (
                    <Button variant="danger" type="button" aria-label={`delete-${r.lessonCode}`} disabled={busy} onClick={() => void remove(r)}>{t('m35.common.delete')}</Button>
                  )}
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
